<!-- Booklet — a printable booklet of one cook's kept recipes, to hand to the
     rest of the family. Needs at least three kept recipes; below that, an
     honest empty state. A printed page cannot play audio, so every margin
     note becomes readable text in the margin column rather than a pen
     stroke — on screen here as well as on paper, since this screen's whole
     purpose is the print preview. -->
<script setup>
import { ref, computed, onMounted } from 'vue';
import {
  IonPage, IonHeader, IonToolbar, IonButtons, IonBackButton, IonContent,
} from '@ionic/vue';
import { useRoute, useRouter } from 'vue-router';
import {
  findCook, cooksReady,
  recipes, recipesReady,
  ingredients, ingredientsReady,
  steps, stepsReady,
  marginNotes, marginNotesReady,
} from '../store.js';
import { formatQuantity } from '../lib/quantity.js';

const route = useRoute();
const router = useRouter();
const backToBook = () => router.push({ name: 'cook-book', params: { cookId: cookId.value } });

// `route.params.cookId`, never `route.params.id` — this route is
// `/cook/:cookId/booklet` and never sets `id`. The generated version read
// `id`, which is always undefined, so `findCook(undefined)` returned null and
// this screen showed "Not found" for every cook that actually exists.
const cookId = computed(() => route.params.cookId);

const loaded = ref(false);
onMounted(async () => {
  await Promise.all([cooksReady, recipesReady, ingredientsReady, stepsReady, marginNotesReady]);
  loaded.value = true;
});

const cook = computed(() => findCook(cookId.value));

// Only kept recipes go into the family's printed copy — one still
// "capturing" is a draft, not something to hand around yet.
const keptRecipes = computed(() =>
  recipes.value.filter((r) => String(r.cookId) === String(cookId.value) && r.status === 'kept'));

function noteText(note) {
  if (note.body) return note.body;
  if (note.kind === 'written') return '';
  return `(${note.kind} note, not transcribed)`;
}

const printable = computed(() => {
  return keptRecipes.value.map((recipe) => {
    const recipeIngredients = ingredients.value
      .filter((i) => String(i.recipeId) === String(recipe.id))
      .slice()
      .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0));
    const recipeSteps = steps.value
      .filter((s) => String(s.recipeId) === String(recipe.id))
      .slice()
      .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0));
    const recipeNotes = marginNotes.value.filter((n) => String(n.recipeId) === String(recipe.id));

    // A note recorded before the first step has no stepId — it belongs to the
    // recipe and sits at the top of the margin, so it becomes a row of its
    // own ahead of step one rather than living outside the ruled list.
    const leadNotes = recipeNotes.filter((n) => !n.stepId);
    const rows = [
      ...(leadNotes.length ? [{ id: `${recipe.id}-lead`, body: '', notes: leadNotes }] : []),
      ...recipeSteps.map((s) => ({
        id: s.id,
        body: s.body,
        notes: recipeNotes.filter((n) => n.stepId && String(n.stepId) === String(s.id)),
      })),
    ];

    return { recipe, ingredients: recipeIngredients, rows };
  });
});

function print() {
  window.print();
}
</script>

<template>
  <IonPage>
    <IonHeader class="hd-bar hd-booklet-hide-print">
      <IonToolbar>
        <IonButtons slot="start">
          <IonBackButton :default-href="`/cook/${cookId}`" text="Book" />
        </IonButtons>
      </IonToolbar>
    </IonHeader>

    <IonContent :fullscreen="true" class="hd-content">
      <div v-if="!loaded" class="hd-page waiting" aria-hidden="true"></div>

      <div v-else-if="!cook" class="hd-page missing hd-booklet-hide-print">
        <h2 class="missing-h">Not found</h2>
        <p class="missing-p">That cook is no longer here. They may have been removed since this link was made.</p>
      </div>

      <div v-else class="hd-page booklet hd-booklet-root">
        <header class="head hd-booklet-hide-print">
          <h1 class="title">The {{ cook.name }} booklet</h1>
          <p v-if="!keptRecipes.length" class="sub">
            Nothing in {{ cook.name }}’s book is finished yet. Write one down and it will be here to print.
          </p>
          <p v-else class="sub">
            <span class="hd-figure count">{{ keptRecipes.length }}</span>
            {{ keptRecipes.length === 1 ? 'recipe' : 'recipes' }}, ready to print and hand around.
          </p>
        </header>

        <button
          v-if="keptRecipes.length"
          type="button"
          class="make-btn hd-booklet-hide-print"
          @click="print"
        >
          Make the booklet
        </button>
        <button
          v-else
          type="button"
          class="make-btn hd-booklet-hide-print"
          @click="backToBook"
        >
          Back to the book
        </button>

        <article
          v-for="entry in printable"
          :key="entry.recipe.id"
          class="recipe hd-booklet-recipe"
        >
          <header class="r-head">
            <h2 class="r-title">{{ entry.recipe.title }}</h2>
            <p class="r-attr">
              {{ cook.name }}<template v-if="entry.recipe.occasion"> · {{ entry.recipe.occasion }}</template>
            </p>
          </header>

          <section v-if="entry.ingredients.length" class="ingredients">
            <h3 class="section-h">Serves {{ entry.recipe.serves || '—' }}</h3>
            <ul class="ing-list">
              <li v-for="ing in entry.ingredients" :key="ing.id" class="ing">
                <span class="hd-figure qty">{{ formatQuantity(ing) || '—' }}</span>
                <span>{{ ing.item }}</span>
              </li>
            </ul>
          </section>

          <h3 v-if="entry.rows.length" class="section-h ruled-h">Method</h3>
          <ol v-if="entry.rows.length" class="steps">
            <li v-for="row in entry.rows" :key="row.id" class="step-row">
              <div class="margin-col">
                <p v-for="note in row.notes" :key="note.id" class="footnote">{{ noteText(note) }}</p>
              </div>
              <p v-if="row.body" class="step-text">{{ row.body }}</p>
            </li>
          </ol>
        </article>
      </div>
    </IonContent>
  </IonPage>
</template>

<style scoped>
.waiting { min-height: 100%; }
.missing { padding: 48px 20px; min-height: 100%; text-align: center; color: var(--hd-pencil); }
.missing-h { margin: 0 0 8px; font-size: var(--hd-t-heading); font-weight: 500; color: var(--hd-ink); }
.missing-p { margin: 0 auto; max-width: 26em; font-style: italic; }

.booklet { padding: 24px 20px 64px calc(var(--hd-margin-w) + 20px); min-height: 100%; position: relative; }
.booklet::before {
  content: '';
  position: absolute;
  top: 0; bottom: 0; left: var(--hd-margin-w);
  width: 1px;
  background: var(--hd-margin);
}

.title { margin: 0; font-size: var(--hd-t-title); font-weight: 500; line-height: 1.15; letter-spacing: -0.01em; }
.sub { margin: 4px 0 0; max-width: 30em; color: var(--hd-pencil); }
.count { font-size: var(--hd-t-small); color: var(--hd-ink); }
.head { margin-bottom: 20px; }

.make-btn {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  padding: 0 4px;
  margin-bottom: 28px;
  background: none;
  border: 0;
  color: var(--hd-margin);
  font-family: var(--hd-sans);
  font-weight: 600;
  font-size: var(--hd-t-small);
  cursor: pointer;
}

.recipe { margin-left: calc(var(--hd-margin-w) * -1 - 20px); }
.recipe + .recipe { margin-top: 40px; padding-top: 28px; border-top: 1px solid var(--hd-feint); }

.r-head, .ingredients { padding-left: calc(var(--hd-margin-w) + 14px); }
.r-title { margin: 0; font-size: var(--hd-t-heading); font-weight: 500; line-height: 1.15; letter-spacing: -0.01em; }
.r-attr { margin: 4px 0 18px; font-style: italic; color: var(--hd-pencil); }

.section-h { margin: 0 0 8px; font-family: var(--hd-sans); font-size: var(--hd-t-figure); font-weight: 600; color: var(--hd-pencil); }
.ruled-h { padding-left: calc(var(--hd-margin-w) + 14px); margin: 18px 0 6px; }
.ing-list { margin: 0 0 8px; padding: 0; list-style: none; }
.ing { display: grid; grid-template-columns: 88px 1fr; gap: 12px; padding: 3px 0; font-size: var(--hd-t-body); line-height: 24px; }
.qty { text-align: right; color: var(--hd-ink); }

.steps {
  margin: 0; padding: 6px 0 0; list-style: none;
  background-image: linear-gradient(var(--hd-feint) 1px, transparent 1px);
  background-size: 100% var(--hd-rule);
  background-position: 0 calc(var(--hd-rule) - 1px);
}
.step-row {
  display: grid;
  grid-template-columns: var(--hd-margin-w) 1fr;
  align-items: start;
}
.margin-col { padding: 2px 4px 6px 0; border-right: 1px solid var(--hd-margin); min-height: var(--hd-rule); }
.step-text { margin: 0; padding: 0 0 8px 14px; font-size: var(--hd-t-step); line-height: var(--hd-rule); max-width: 34em; }

.footnote {
  margin: 0 0 4px;
  font-family: var(--hd-sans);
  font-size: var(--hd-t-micro);
  line-height: 1.3;
  color: var(--hd-pencil);
}
</style>

<!-- Unscoped deliberately: this rule has to reach the tab bar, which the
     shell renders as a sibling outside this component's own subtree. Loaded
     once, as soon as this page is registered, because product-routes.js
     imports every page eagerly. -->
<style>
@media print {
  ion-tab-bar { display: none !important; }
  .hd-booklet-hide-print { display: none !important; }
  .hd-booklet-root { background: none; }
  .hd-booklet-recipe { break-before: page; }
  .hd-booklet-recipe:first-of-type { break-before: auto; }
}
</style>
